import React, { useMemo } from 'react';
import {
  ChoiceButton,
  ChoiceButtonGroup,
  DateTimePreview,
  FormGroup,
  Input,
  Label,
} from '../../../../styles/lesson/lesson.style';
import {
  useLessonCreationState,
  useLessonCreationDispatch,
} from '../lessonCreation.state';
import { IntroCallHint } from '../../../register/InfoBlock';
import { formatInTimeZone } from 'date-fns-tz';
import { wallClockToISO, shortTzName, friendlyTzName, formatRangeInTz } from '../../../../utils/timezones';

// --- Utils ---

const blockPickerHotkeys = (e: React.KeyboardEvent<HTMLInputElement>): void => {
  const isSpace = e.key === ' ' || e.key === 'Spacebar' || e.code === 'Space';
  const isEnter = e.key === 'Enter';
  const isAltDown = e.altKey && e.key === 'ArrowDown';
  if (isSpace || isEnter || isAltDown) {
    e.preventDefault();
    e.stopPropagation();
  }
};

// --- Component ---

export const Step0_IntroChoice: React.FC = () => {
  const { introMeetingChoice, introDate, introTime, eventName, tutorName, tutorTimezone, user, existingEvents, tutorBusySlots, activeInputTz } =
    useLessonCreationState();
  const dispatch = useLessonCreationDispatch();
  const inputTz = activeInputTz;
  const tutorTzDiffers = inputTz !== tutorTimezone;

  // --- Handlers ---
  const handleChoice = (choice: 'yes' | 'no') => {
    dispatch({ type: 'SET_INTRO_CHOICE', payload: choice });
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: 'SET_INTRO_DATE', payload: e.target.value });
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: 'SET_INTRO_TIME', payload: e.target.value });
  };

  const handleEventNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: 'SET_EVENT_NAME', payload: e.target.value });
  };

  // --- Timezone-correct preview ---
  const introPreview = useMemo(() => {
    if (introDate && introTime) {
      try {
        const utc = new Date(wallClockToISO(introDate, introTime, inputTz));
        if (!isNaN(utc.getTime())) {
          return formatInTimeZone(utc, inputTz, 'EEEE d MMMM yyyy, HH:mm');
        }
      } catch { /* invalid */ }
    }
    return '';
  }, [introDate, introTime, inputTz]);

  // --- Cross-timezone preview (show tutor's time) ---
  const tutorTimePreview = useMemo(() => {
    if (!tutorTzDiffers || !introDate || !introTime) return null;
    try {
      const startUtc = new Date(wallClockToISO(introDate, introTime, inputTz));
      const endUtc = new Date(startUtc.getTime() + 15 * 60 * 1000);
      return formatRangeInTz(startUtc, endUtc, tutorTimezone, { includeDate: true });
    } catch { return null; }
  }, [tutorTzDiffers, introDate, introTime, inputTz, tutorTimezone]);

  // Validate intro call doesn't start in the past
  const introStartInPast = useMemo(() => {
    if (!introDate || !introTime) return false;
    try {
      return new Date(wallClockToISO(introDate, introTime, inputTz)).getTime() <= Date.now();
    } catch { return false; }
  }, [introDate, introTime, inputTz]);

  // Check for scheduling conflicts (intro call = 30 mins)
  const introConflict = useMemo(() => {
    if (!introDate || !introTime) return null;
    try {
      const newStart = wallClockToISO(introDate, introTime, inputTz);
      const startDt = new Date(newStart);
      const newEnd = new Date(startDt.getTime() + 15 * 60 * 1000).toISOString();

      const parentHit = existingEvents.find(ev => ev.startAt < newEnd && ev.endAt > newStart);
      if (parentHit) return `You already have "${parentHit.title}" booked at this time.`;

      const tutorHit = tutorBusySlots.find(s => s.startAt < newEnd && s.endAt > newStart);
      if (tutorHit) return `${tutorName || 'The tutor'} already has a booking at this time.`;

      return null;
    } catch { return null; }
  }, [introDate, introTime, tutorTimezone, existingEvents, tutorBusySlots, tutorName]);

  // Timezone label
  const tzLabel = useMemo(() => {
    return friendlyTzName(inputTz);
  }, [inputTz]);

  // --- Render ---
  return (
    <>
      <IntroCallHint tutorName={tutorName} />
      <ChoiceButtonGroup>
        <ChoiceButton
          isSelected={introMeetingChoice === 'yes'}
          onClick={() => handleChoice('yes')}
        >
          Yes, schedule a call
        </ChoiceButton>
        <ChoiceButton
          isSelected={introMeetingChoice === 'no'}
          onClick={() => handleChoice('no')}
        >
          No, create a lesson instead
        </ChoiceButton>
      </ChoiceButtonGroup>

      {introMeetingChoice === 'yes' && (
        <>
          {/* Timezone context */}
          <p style={{ color: '#6b7280', fontSize: '0.8rem', margin: '0.75rem 0 0.25rem', lineHeight: 1.4 }}>
            Times in your timezone ({tzLabel})
          </p>

          <FormGroup>
            <Label css={{ marginBottom: '1rem' }} htmlFor="introDateTime">
              Pick a date and time for the call
            </Label>
            <Input
              css={{ marginBottom: '1.5rem' }}
              type="date"
              id="introDate"
              name="date"
              value={introDate}
              min={new Date().toISOString().split('T')[0]}
              onChange={handleDateChange}
              onKeyDown={blockPickerHotkeys}
            />
            <Input
              type="time"
              id="introTime"
              name="time"
              value={introTime}
              onChange={handleTimeChange}
              onKeyDown={blockPickerHotkeys}
            />
            {introStartInPast && (
              <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.5rem 0 0' }}>
                Call cannot start in the past.
              </p>
            )}
            {introConflict && !introStartInPast && (
              <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.5rem 0 0' }}>
                {introConflict} Please choose a different time.
              </p>
            )}
            <DateTimePreview>{introPreview}</DateTimePreview>
            {tutorTimePreview && (
              <p style={{ color: '#6b7280', fontSize: '0.8rem', textAlign: 'center', marginTop: '0.25rem' }}>
                {tutorName ? `${tutorName}'s` : "Tutor's"} time: {tutorTimePreview}
              </p>
            )}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="eventName">Title</Label>
            <Input
              type="text"
              id="eventName"
              value={eventName}
              onChange={handleEventNameChange}
            />
          </FormGroup>
        </>
      )}
    </>
  );
};
